
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import {mapActions, mapGetters, mapState} from 'vuex';
    import _ from 'lodash';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import { Termin, Terminkalender } from '@/types/Terminkalender';
    import axios, { AxiosResponse, CancelTokenSource } from 'axios';
    import api from '@/services/Api';
    import Patient from '@/types/Patient';
    import { confirmSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';
    import { ValidationRules } from '@/store/types';
    import { VForm } from '@/types/Components';
    import { PatientSearchItem } from '@/components/Impfverwaltung/Patient/PatientSearch.vue';
    import Terminart from '@/types/Terminart';
    import moment from 'moment';

    @Component({
        components: {
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('terminkalender', ['kalender', 'loaders']),
            ...mapGetters('authentication', ['getSystemEinstellung']),
        },
        methods: {
            ...mapActions('terminkalender', ['updateTermin', 'deleteTermin', 'deleteTerminserie']),
            ...mapActions('patient', ['loadPatient']),
        },
    })
    export default class TerminkalenderDetail extends Vue {
        @Prop() value!: Termin | null;
        @Prop() visible!: boolean;

        public $refs!: {
            form: VForm;
        };

        public kalender!: Terminkalender;
        public rules!: ValidationRules;
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public loadPatient!: (payload: { patnr: number }) => Promise<void>;
        public updateTermin!: (termin: Termin) => void;
        public deleteTermin!: (termin: Termin) => void
        public deleteTerminserie!: (termin: Termin) => void

        public termin = new Termin();
        public patient: PatientSearchItem | string | null = '';
        public zeitraumErrorMessages: string | null = '';

        public search = '';
        public validSearch = true;
        public loading = false;
        public items: PatientSearchItem[] = [];
        public searchApiCall: _.DebouncedFunc<(query: string) => void> | null = null;
        public source: CancelTokenSource | null = null;

        public datumMenu = false;
        public terminserie = false;
        public datumFormatted: string | null = null;
        public datumErrorMessages: string[] = [];

        public mounted(): void {
            this.search = '';
            this.validSearch = true;
            this.items = [];
        }

        public get isNew(): boolean {
            return this.termin.termin_id === null;
        }

        public get patientName(): string {
            return typeof this.patient === 'object' ? this.patient?.name ?? '' : this.patient;
        }

        public get isTerminartFrei(): boolean {
            const terminart = this.kalender.arten.find((art) => art.terminart === this.termin.terminart);
            return terminart?.bezeichnung?.toLocaleUpperCase() === Terminart.Frei;
        }

        @Watch('isTerminartFrei')
        public onChangeIsTerminartFrei(isTerminartFrei: boolean): void {
            if (isTerminartFrei) {
                this.patient = '';
                this.termin.hinweis = '';
            }
        }

        public getStandardTerminart(): any {
            for (var cnt = 0; this.kalender.arten.length > cnt; cnt++) {
                if (this.kalender.arten[cnt].standard === 'j') {
                    this.termin.terminart = this.kalender.arten[cnt].terminart;
                }
            }
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                if (this.value) {
                    this.termin = new Termin(this.value);
                } else {
                    this.termin = new Termin();
                }

                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }

                if (this.isNew && !this.termin.terminart) {
                    this.getStandardTerminart();
                }

                if (this.termin.terminserie_id) {
                    this.terminserie = true;
                } else {
                    this.terminserie = false;
                }

                this.searchApiCall = null;
                this.validSearch = true;
                this.datumErrorMessages = [];
                this.datumFormatted = this.formatDate(this.termin.datum);

                if (this.termin.patnr) {
                    const patientItem = {
                        name: (this.termin.pat_nachname ?? '') + ' ' + (this.termin.pat_vorname ?? ''),
                        id: this.termin.patnr,
                        geburtsDatum: this.termin.pat_geburtsdatum ?? '',
                        adresse: this.termin.pat_strasse ?? '',
                    };

                    this.patient = patientItem;
                    this.items = [{ ...patientItem }];
                } else {
                    this.patient = this.termin.vorname ?? '';
                    this.items = [];
                }
            } else {
                if (this.source) {
                    this.source.cancel('cancel ivs/patienten/search');
                }
            }
        }

        @Watch('search', { immediate: true })
        public onChangeSearch(search: string): void {
            if (search && search !== this.patientName) {
                this.querySelections(search);
            }
        }

        @Watch('terminserie')
        public isTerminSerie() {
            if (this.terminserie) {
                if (!this.termin.seriendauer_von) {
                    this.termin.seriendauer_von = this.termin.datum;
                }

                if (!this.termin.seriendauer_bis && this.termin.datum) {
                    this.termin.seriendauer_bis = moment(this.termin.datum).add(1, 'month').format('YYYY-MM-DD');
                }
            }
        }

        @Watch('termin.datum')
        public onChangeImpfdatum(date: string | null): void {
            this.datumFormatted = this.formatDate(this.termin.datum);

            if (date !== null && this.isNew) {
                this.validateDatum();
            }

            if (this.terminserie) {
                this.termin.seriendauer_von = this.termin.datum;
                this.termin.seriendauer_bis = moment(this.termin.datum).add(1, 'month').format('YYYY-MM-DD');
            }
        }

        @Watch('termin.seriendauer_bis')
        public onChangeSeriendauerBis(): void {
            if (this.termin.seriendauer_von && this.termin.seriendauer_bis) {
                if (Math.abs(moment(this.termin.seriendauer_von).diff(moment(this.termin.seriendauer_bis), 'days')) > 365) {
                    this.zeitraumErrorMessages = 'Zeitraum darf nicht mehr als 365 Tage überschreiten.';
                    this.termin.seriendauer_bis = null;
                } else {
                    this.zeitraumErrorMessages = '';
                }
            }
        }

        public validateDatum(): void {
            this.datumErrorMessages = [];

            for (const rule of [this.rules.required, this.rules.validDate]) {
                const valid = rule(this.termin.datum);
                if (valid !== true) {
                    this.datumErrorMessages.push(valid as string);
                }
            }
        }

        public querySelections(query: string): void {
            this.validSearch = query.trim().length >= 3 && query[0] !== ' ';

            if (!this.validSearch) {
                this.items = [];
                return;
            }

            this.loading = true;

            if (!this.searchApiCall) {
                this.searchApiCall = _.debounce((query: string) => {
                    if (this.source) {
                        this.source.cancel('cancel ivs/patienten/search');
                    }

                    this.source = axios.CancelToken.source();

                    api.post('ivs/patienten/search', { query }, { cancelToken: this.source.token })
                        .then((res: AxiosResponse) => {
                            const items: PatientSearchItem[] = [];

                            res.data.body
                                .map((data: Partial<Patient>) => new Patient(data))
                                .forEach((patient: Patient, idx: number) => {
                                    items.push({
                                        name: patient.nachname + ' ' + patient.vorname,
                                        id: patient.patnr as number,
                                        geburtsDatum: patient.geburtsdatum ?? '',
                                        adresse: patient.Adresse,
                                        telefon: patient.telefon ?? '',
                                        telefon2: patient.telefon2 ?? '',
                                    });

                                    if (idx !== res.data.body.length - 1) {
                                        items.push({ divider: true });
                                    }
                                });

                            this.items = items;
                            this.loading = false;
                        })
                        .catch(console.error)
                        .finally(() => {
                            this.source = null;
                        });
                }, 500);
            }
            this.searchApiCall(query);
        }

        public onClickPatient(patnr: number): void {
            this.$emit('close');
            this.$router.push('/');
            this.loadPatient({ patnr });
        }

        public onClickSave(): void {
            this.validateDatum();

            if (this.$refs.form.validate() && this.datumErrorMessages.length === 0) {
                if (typeof this.patient === 'string' && this.patient !== this.search) {
                    this.patient = this.search;
                }

                if (typeof this.patient === 'string') {
                    this.termin.patnr = null;
                }

                this.updateTermin(new Termin({
                    ...this.termin,
                    patnr: typeof this.patient === 'object'
                        ? this.patient?.id ?? null
                        : this.termin.patnr,
                    vorname: !this.isTerminartFrei ? this.patientName : this.termin.vorname,
                }));
            }
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Termin endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteTermin(this.termin);
                    }
                });
        }

        public onClickDeleteTerminserie(): void {
            confirmSwal('Löschen', 'Terminserie endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteTerminserie(this.termin);
                    }
                });
        }

        public onPatientSelected(selectedPatient: PatientSearchItem | string | null): void {
            if (selectedPatient != null && typeof selectedPatient === 'object') {
                if (this.termin.telefon == null || this.termin.telefon.trim().length === 0) {
                    const hasTelefon = selectedPatient.telefon != null && selectedPatient.telefon.trim().length > 0;
                    const hasTelefon2 = selectedPatient.telefon2 != null && selectedPatient.telefon2.trim().length > 0;
                    this.termin.telefon = hasTelefon ? (selectedPatient.telefon ?? null)
                        : (hasTelefon2 ? (selectedPatient.telefon2 ?? null) : null)
                }
            }
        }
    }
